import { useState, useEffect, Component } from "react";
import { useAuth } from "../App";
import { FetchErrorState } from "../components/FetchErrorState";
import { AppLayout } from "./DashboardPage";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  AlertCircle,
  Check,
  CheckCircle2,
  Copy,
  ArrowLeftRight,
  Tag,
  MoreVertical,
  ChevronDown,
  ArrowUpRight,
  ArrowDownRight,
  Filter,
  X,
  Loader2,
  Sparkles,
  RefreshCw
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Page-level error boundary
class ReviewQueueErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('[ReviewQueue] Render error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <AppLayout>
          <div className="max-w-lg mx-auto mt-16 p-6">
            <div className="card-dashboard text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-lg font-heading font-semibold mb-2">Something didn't go through</h2>
              <p className="text-sm text-muted-foreground mb-4">
                This page encountered an error while loading. Please try refreshing.
              </p>
              <pre className="text-left bg-muted p-3 rounded text-xs overflow-auto mb-4 max-h-32">
                {this.state.error?.message || 'Unknown error'}
              </pre>
              <div className="flex gap-2 justify-center">
                <Button onClick={() => window.location.reload()}>Refresh Page</Button>
                <Button variant="outline" onClick={() => window.location.href = '/dashboard'}>Back to Overview</Button>
              </div>
            </div>
          </div>
        </AppLayout>
      );
    }
    return this.props.children;
  }
}

export function ReviewQueueContent() {
  return (
    <ReviewQueueErrorBoundary>
      <ReviewQueueInner embedded />
    </ReviewQueueErrorBoundary>
  );
}

export default function ReviewQueuePage() {
  return (
    <ReviewQueueErrorBoundary>
      <ReviewQueueInner />
    </ReviewQueueErrorBoundary>
  );
}

function ReviewQueueInner({ embedded = false }) {
  const { household } = useAuth();
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [queueData, setQueueData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedTransactions, setSelectedTransactions] = useState(new Set());
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkCategory, setBulkCategory] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!household) return;
    fetchData();
  }, [household]);

  const fetchData = async () => {
    setFetchError(false);
    console.log("[ReviewQueue] Fetching for household:", household?.household_id);
    try {
      const [queueRes, catRes] = await Promise.all([
        fetch(`${API_URL}/api/households/${household.household_id}/review-queue`, { credentials: 'include' }),
        fetch(`${API_URL}/api/households/${household.household_id}/categories`, { credentials: 'include' })
      ]);

      if (queueRes.ok) {
        const data = await queueRes.json();
        console.log("[ReviewQueue] Loaded:", data?.stats?.total_pending || 0, "pending items");
        setQueueData(data || { uncategorized: [], needs_review: [], potential_duplicates: [], potential_transfers: [], stats: {} });
      } else {
        console.error('[ReviewQueue] API returned', queueRes.status);
        setFetchError(true);
      }
      if (catRes.ok) {
        const cats = await catRes.json();
        setCategories(Array.isArray(cats) ? cats : []);
      }
    } catch (error) {
      console.error('[ReviewQueue] Network error:', error);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const formatCurrency = (amount) => {
    const currency = household?.currency || 'NZD';
    return new Intl.NumberFormat('en-NZ', { style: 'currency', currency }).format(amount);
  };

  const toggleSelection = (txId) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(txId)) {
      newSelected.delete(txId);
    } else {
      newSelected.add(txId);
    }
    setSelectedTransactions(newSelected);
  };

  const selectAll = (transactions) => {
    const ids = transactions.map(tx => tx.transaction_id);
    setSelectedTransactions(new Set(ids));
  };

  const clearSelection = () => {
    setSelectedTransactions(new Set());
  };

  const handleBulkUpdate = async () => {
    if (selectedTransactions.size === 0) return;
    
    setProcessing(true);
    try {
      const updates = {
        transaction_ids: Array.from(selectedTransactions)
      };

      if (bulkAction === 'categorize' && bulkCategory) {
        updates.category_id = bulkCategory;
        updates.status = 'reviewed';
      } else if (bulkAction === 'mark_reviewed') {
        updates.status = 'reviewed';
      } else if (bulkAction === 'mark_transfer') {
        updates.is_transfer = true;
        updates.status = 'reviewed';
      }

      const response = await fetch(`${API_URL}/api/households/${household.household_id}/transactions/bulk-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.count} sorted — you're all up to date`);
        setShowBulkDialog(false);
        setBulkAction(null);
        setBulkCategory("");
        clearSelection();
        fetchData();
      }
    } catch (error) {
      toast.error("That didn't work — please try again");
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkTransferPair = async (txIds) => {
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/transactions/mark-transfer-pair`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ transaction_ids: txIds })
      });

      if (response.ok) {
        toast.success("Matched as a transfer");
        fetchData();
      }
    } catch (error) {
      console.error('Mark transfer error:', error);
      toast.error("Could not mark as transfer. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const handleDismissDuplicate = async (txIds) => {
    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/transactions/dismiss-duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ transaction_ids: txIds })
      });

      if (response.ok) {
        toast.success("Dismissed — not a duplicate");
        fetchData();
      }
    } catch (error) {
      console.error('Dismiss duplicate error:', error);
      toast.error("Could not dismiss duplicate. Please try again.");
    }
  };

  const handleQuickCategorize = async (txId, categoryId) => {
    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/transactions/${txId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ category_id: categoryId, status: 'reviewed' })
      });

      if (response.ok) {
        const data = await response.json();
        const catName = data.category_name || categories.find(c => c.category_id === categoryId)?.name || 'Unknown';
        toast(
          <div className="flex items-center justify-between gap-3" data-testid="categorized-toast">
            <span className="text-sm">Nice — this one's sorted</span>
            <button
              className="text-xs font-medium text-red-500 hover:text-red-600 whitespace-nowrap"
              onClick={() => handleUndoCategorize(txId)}
              data-testid="undo-categorize-btn"
            >
              Undo
            </button>
          </div>,
          { duration: 5000 }
        );
        fetchData();
      }
    } catch (error) {
      console.error('Categorize error:', error);
      toast.error("Could not categorize transaction. Please try again.");
    }
  };

  const handleUndoCategorize = async (txId) => {
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/transactions/${txId}/undo-categorize`, {
        method: 'POST', credentials: 'include'
      });
      if (!res.ok) throw new Error('Could not undo');
      toast.dismiss();
      toast.success('Category cleared — back to how it was');
      fetchData();
    } catch (e) {
      console.error('Undo categorize error:', e);
      toast.error(e.message || 'Could not undo. Please try again.');
    }
  };

  if (loading) {
    return embedded ? (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    ) : (
      <AppLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  if (fetchError) {
    const errorContent = (
      <div className="p-6 lg:p-8">
        <FetchErrorState onRetry={fetchData} message="This looks like a connection issue — your data is safe. Try again." />
      </div>
    );
    return embedded ? errorContent : <AppLayout>{errorContent}</AppLayout>;
  }

  const stats = queueData?.stats || {};
  const totalPending = stats.total_pending || 0;
  const progressPercent = totalPending > 0 ? 0 : 100;

  const content = (
      <div className="p-6 lg:p-8" data-testid="review-queue-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold">What needs your attention</h1>
            <p className="text-muted-foreground">
              {totalPending > 0 
                ? `${totalPending} things could use a quick look`
                : "You're all caught up — nothing needs your attention right now"}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            
            {selectedTransactions.size > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className="btn-primary" data-testid="bulk-actions-btn">
                    <Check className="w-4 h-4 mr-2" />
                    Bulk Actions ({selectedTransactions.size})
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => { setBulkAction('categorize'); setShowBulkDialog(true); }}>
                    <Tag className="w-4 h-4 mr-2" />
                    Sort these
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setBulkAction('mark_reviewed'); handleBulkUpdate(); }}>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    All good
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setBulkAction('mark_transfer'); handleBulkUpdate(); }}>
                    <ArrowLeftRight className="w-4 h-4 mr-2" />
                    Mark as Transfer
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={clearSelection} className="text-muted-foreground">
                    <X className="w-4 h-4 mr-2" />
                    Clear Selection
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="card-dashboard">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Needs a category</p>
                <p className="font-heading text-2xl font-bold">{stats.uncategorized_count || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="card-dashboard">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                <Tag className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Quick check</p>
                <p className="font-heading text-2xl font-bold">{stats.needs_review_count || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="card-dashboard">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center">
                <Copy className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Duplicates</p>
                <p className="font-heading text-2xl font-bold">{stats.duplicate_pairs || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="card-dashboard">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                <ArrowLeftRight className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Transfers</p>
                <p className="font-heading text-2xl font-bold">{stats.transfer_pairs || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="uncategorized" className="space-y-6">
          <TabsList>
            <TabsTrigger value="uncategorized" data-testid="tab-uncategorized">
              Needs a category ({stats.uncategorized_count || 0})
            </TabsTrigger>
            <TabsTrigger value="needs_review" data-testid="tab-needs-review">
              Quick check ({stats.needs_review_count || 0})
            </TabsTrigger>
            <TabsTrigger value="duplicates" data-testid="tab-duplicates">
              Duplicates ({stats.duplicate_pairs || 0})
            </TabsTrigger>
            <TabsTrigger value="transfers" data-testid="tab-transfers">
              Transfers ({stats.transfer_pairs || 0})
            </TabsTrigger>
          </TabsList>

          {/* Uncategorized Tab */}
          <TabsContent value="uncategorized" className="space-y-4">
            {(queueData?.uncategorized || []).length === 0 ? (
              <div className="card-dashboard text-center py-12">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                <h3 className="font-heading font-semibold mb-2">You're all caught up</h3>
                <p className="text-sm text-muted-foreground">Nothing needs a category right now</p>
              </div>
            ) : (
              <div className="card-dashboard">
                <div className="flex items-center justify-between mb-4">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => selectAll(queueData.uncategorized)}
                  >
                    Select All
                  </Button>
                </div>
                
                <div className="space-y-1">
                  {(queueData?.uncategorized || []).map((tx) => (
                    <div 
                      key={tx.transaction_id}
                      className={`flex items-center gap-4 p-4 rounded-lg border transition-colors ${
                        selectedTransactions.has(tx.transaction_id)
                          ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                          : 'border-transparent hover:bg-muted/30'
                      }`}
                      data-testid={`uncategorized-${tx.transaction_id}`}
                    >
                      <Checkbox
                        checked={selectedTransactions.has(tx.transaction_id)}
                        onCheckedChange={() => toggleSelection(tx.transaction_id)}
                      />
                      
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        tx.amount > 0 
                          ? 'bg-emerald-100 dark:bg-emerald-900/30' 
                          : 'bg-stone-100 dark:bg-stone-800/30'
                      }`}>
                        {tx.amount > 0 ? (
                          <ArrowDownRight className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <ArrowUpRight className="w-5 h-5 text-stone-600 dark:text-stone-400" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{tx.description}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(tx.date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })}
                          {tx.merchant && ` • ${tx.merchant}`}
                        </p>
                      </div>

                      <span className={`font-medium tabular-nums ${tx.amount > 0 ? 'text-emerald-600' : ''}`}>
                        {tx.amount > 0 ? '+' : ''}{formatCurrency(tx.amount)}
                      </span>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" data-testid={`categorize-dropdown-${tx.transaction_id}`}>
                            <Tag className="w-4 h-4 mr-2" />
                            Sort this
                            <ChevronDown className="w-4 h-4 ml-2" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="max-h-64 overflow-y-auto">
                          {categories.map(cat => (
                            <DropdownMenuItem 
                              key={cat.category_id}
                              onClick={() => handleQuickCategorize(tx.transaction_id, cat.category_id)}
                            >
                              {cat.name}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Needs Review Tab */}
          <TabsContent value="needs_review" className="space-y-4">
            {(queueData?.needs_review || []).length === 0 ? (
              <div className="card-dashboard text-center py-12">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                <h3 className="font-heading font-semibold mb-2">You're all caught up</h3>
                <p className="text-sm text-muted-foreground">Nothing needs your attention right now</p>
              </div>
            ) : (
              <div className="card-dashboard">
                <div className="flex items-center justify-between mb-4">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => selectAll(queueData.needs_review)}
                  >
                    Select All
                  </Button>
                </div>
                
                <div className="space-y-1">
                  {(queueData?.needs_review || []).map((tx) => (
                    <div 
                      key={tx.transaction_id}
                      className={`flex items-center gap-4 p-4 rounded-lg border transition-colors ${
                        selectedTransactions.has(tx.transaction_id)
                          ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                          : 'border-transparent hover:bg-muted/30'
                      }`}
                    >
                      <Checkbox
                        checked={selectedTransactions.has(tx.transaction_id)}
                        onCheckedChange={() => toggleSelection(tx.transaction_id)}
                      />
                      
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        tx.amount > 0 
                          ? 'bg-emerald-100 dark:bg-emerald-900/30' 
                          : 'bg-stone-100 dark:bg-stone-800/30'
                      }`}>
                        {tx.amount > 0 ? (
                          <ArrowDownRight className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <ArrowUpRight className="w-5 h-5 text-stone-600 dark:text-stone-400" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{tx.description}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(tx.date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })}
                          {tx.category_name && ` • ${tx.category_name}`}
                        </p>
                      </div>

                      <span className={`font-medium tabular-nums ${tx.amount > 0 ? 'text-emerald-600' : ''}`}>
                        {tx.amount > 0 ? '+' : ''}{formatCurrency(tx.amount)}
                      </span>

                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleQuickCategorize(tx.transaction_id, tx.category_id)}
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Looks good
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Duplicates Tab */}
          <TabsContent value="duplicates" className="space-y-4">
            {(queueData?.potential_duplicates || []).length === 0 ? (
              <div className="card-dashboard text-center py-12">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                <h3 className="font-heading font-semibold mb-2">No duplicates</h3>
                <p className="text-sm text-muted-foreground">Everything looks unique</p>
              </div>
            ) : (
              <div className="space-y-4">
                {(queueData?.potential_duplicates || []).map((pair, index) => {
                  // Backend returns transaction_1/transaction_2 objects directly
                  const tx1 = pair.transaction_1 || (pair.transaction_ids ? (queueData.uncategorized || []).concat(queueData.needs_review || []).find(t => t.transaction_id === pair.transaction_ids[0]) : null);
                  const tx2 = pair.transaction_2 || (pair.transaction_ids ? (queueData.uncategorized || []).concat(queueData.needs_review || []).find(t => t.transaction_id === pair.transaction_ids[1]) : null);
                  
                  if (!tx1 || !tx2) return null;

                  return (
                    <div key={index} className="card-dashboard">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Copy className="w-5 h-5 text-purple-500" />
                          <span className="font-medium">Potential Duplicate</span>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDismissDuplicate(pair.transaction_ids || [tx1?.transaction_id, tx2?.transaction_id].filter(Boolean))}
                          >
                            Not a Duplicate
                          </Button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        {[tx1, tx2].filter(Boolean).map((tx) => (
                          <div key={tx.transaction_id} className="p-4 rounded-lg bg-muted/30 border border-border">
                            <p className="font-medium mb-1">{tx.description}</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(tx.date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                            <p className={`font-medium mt-2 ${tx.amount > 0 ? 'text-emerald-600' : ''}`}>
                              {formatCurrency(tx.amount)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Transfers Tab */}
          <TabsContent value="transfers" className="space-y-4">
            {(queueData?.potential_transfers || []).length === 0 ? (
              <div className="card-dashboard text-center py-12">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                <h3 className="font-heading font-semibold mb-2">No transfers</h3>
                <p className="text-sm text-muted-foreground">We didn't spot any matching transfers</p>
              </div>
            ) : (
              <div className="space-y-4">
                {(queueData?.potential_transfers || []).map((pair, index) => {
                  // Backend returns transaction_1/transaction_2 objects directly
                  const tx1 = pair.transaction_1 || (pair.transaction_ids ? (queueData.uncategorized || []).concat(queueData.needs_review || []).find(t => t.transaction_id === pair.transaction_ids[0]) : null);
                  const tx2 = pair.transaction_2 || (pair.transaction_ids ? (queueData.uncategorized || []).concat(queueData.needs_review || []).find(t => t.transaction_id === pair.transaction_ids[1]) : null);
                  
                  if (!tx1 || !tx2) return null;

                  return (
                    <div key={index} className="card-dashboard">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <ArrowLeftRight className="w-5 h-5 text-emerald-500" />
                          <span className="font-medium">Potential Transfer</span>
                          <Badge variant="secondary">{pair.days_apart || 0} days apart</Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDismissDuplicate(pair.transaction_ids || [tx1?.transaction_id, tx2?.transaction_id].filter(Boolean))}
                          >
                            Not a Transfer
                          </Button>
                          <Button 
                            size="sm"
                            className="btn-primary"
                            onClick={() => handleMarkTransferPair(pair.transaction_ids || [tx1?.transaction_id, tx2?.transaction_id].filter(Boolean))}
                            disabled={processing}
                          >
                            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                            Confirm Transfer
                          </Button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        {[tx1, tx2].filter(Boolean).map((tx) => (
                          <div key={tx.transaction_id} className={`p-4 rounded-lg border ${
                            tx.amount > 0 
                              ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/50' 
                              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50'
                          }`}>
                            <div className="flex items-center gap-2 mb-2">
                              {tx.amount > 0 ? (
                                <ArrowDownRight className="w-4 h-4 text-emerald-600" />
                              ) : (
                                <ArrowUpRight className="w-4 h-4 text-red-600" />
                              )}
                              <span className="text-sm font-medium">
                                {tx.amount > 0 ? 'Money In' : 'Money Out'}
                              </span>
                            </div>
                            <p className="font-medium mb-1">{tx.description}</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(tx.date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })}
                            </p>
                            <p className={`font-heading text-xl font-bold mt-2 ${
                              tx.amount > 0 ? 'text-emerald-600' : 'text-red-600'
                            }`}>
                              {tx.amount > 0 ? '+' : ''}{formatCurrency(tx.amount)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Bulk Category Dialog */}
        <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Sort these</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Pick a category for {selectedTransactions.size} transaction{selectedTransactions.size !== 1 ? 's' : ''}
              </p>
              
              <select
                value={bulkCategory}
                onChange={(e) => setBulkCategory(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3"
                data-testid="bulk-category-select"
              >
                <option value="">Select category</option>
                {categories.map(cat => (
                  <option key={cat.category_id} value={cat.category_id}>
                    {cat.name}
                  </option>
                ))}
              </select>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowBulkDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleBulkUpdate}
                  disabled={!bulkCategory || processing}
                  className="btn-primary"
                  data-testid="apply-bulk-category-btn"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
  );

  return embedded ? content : <AppLayout>{content}</AppLayout>;
}
